<template>
  <AppContentLoader v-if="isPreLoading" />

  <div
    v-else
    class="content-to-print h-full"
  >
    <CoverPage :pdf-data="pdfData" />

    <IndexPage
      :all-renderable-sections="allRenderableSections"
      :index-clicked="goToSection"
    />

    <template
      v-for="(section, idx) in allRenderableSections"
      :key="idx"
    >
      <section
        :id="section.id"
        :class="{
          'page': !section.isCategory,
          'has-content': section.content?.length,
          'category': section.isCategory,
          'break-before': section.breakBefore,
          'subsection': section.level > 0
        }"
        :data-page-root-id="section.rootId"
        :data-page-parent-id="section.parent?.id"
        :data-page-id="section.id"
        :data-orientation="section.orientation"
        :style="{ '--label': `'${section.rootId === 'catalytic-projects' ? section.label : section.label}'` }"
      >
        <!--custom header for sections so we can get current section context-->
        <CustomHeader
          :pdf-data="pdfData"
          :section="section"
        />

        <template v-if="!section.isCategory">
          <h1 v-if="section.level === 0 && section.label">
            {{ section.label }}
          </h1>

          <h2 v-else-if="(section.level === 1 || (section.level === 2 && section.rootId === 'catalytic-projects')) && section.label">
            {{ section.label }}
          </h2>

          <template v-if="section.content">
            <template
              v-for="(contentItem, cIdx) in section.content.flat()"
              :key="cIdx"
            >
              <template v-if="contentItem.type === 'heading'">
                <h2
                  v-if="section.id === 'diagnostics'"
                  class="heading"
                >
                  {{ contentItem.value || 'Lorem Ipsum is silly dummy text' }}
                </h2>

                <h3
                  v-else
                  class="heading"
                >
                  {{ contentItem.value || 'Lorem Ipsum is silly dummy text' }}
                </h3>
              </template>

              <!--wizywig content-->
              <div
                v-if="contentItem.type === 'tinymce'"
                class="tinymce-content"
                v-html="domPurify.sanitize((contentItem.value || ''))"
              />

              <div
                v-if="contentItem.type === 'powerbi'"
                :data-powerbi-page-id="contentItem.pageId"
                class="powerbi-container w-full h-full"
              />

              <a
                v-if="contentItem.type === 'link-button' && contentItem.url"
                :href="contentItem.url"
                target="_blank"
                class="website-btn"
              >
                View Official Website
                <i class="fa-regular fa-arrow-right h-5" />
              </a>

              <template v-else-if="contentItem.type === 'table'">
                <ListOfAbbreviations
                  v-if="section.id === 'list-of-abbreviations'"
                  :content-item="contentItem"
                />

                <ListOfFigures
                  v-if="section.id === 'list-of-figures'"
                  :content-item="contentItem"
                />

                <template v-if="section.rootId === 'catalytic-projects'">
                  <CatalyticProjectsDetails
                    v-if="contentItem.style === 'projectDetails'"
                    :content-item="contentItem"
                  />

                  <CatalyticProjectsImpact
                    v-if="contentItem.style === 'developmentImpact'"
                    :content-item="contentItem"
                  />

                  <CatalyticProjectsAlignment
                    v-if="contentItem.style === 'alignments' && contentItem.rows.length"
                    :content-item="contentItem"
                  />
                </template>
              </template>

              <template v-else-if="contentItem.type === 'list'">
                <CategoriesList
                  v-if="section.rootId === 'appendix'"
                  :content-item="contentItem"
                />
              </template>

              <template v-if="contentItem.type === 'card'">
                <CatalyticProjects
                  v-if="section.id === 'catalytic-projects'"
                  :content-item="contentItem"
                />
              </template>
            </template>
          </template>
        </template>
      </section>
    </template>
  </div>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { Previewer, Handler, registerHandlers } from 'pagedjs';
import * as pbi from 'powerbi-client';
import { nextTick, onMounted, onUnmounted, ref } from 'vue';
import { useRoute } from 'vue-router';

import { AppContentLoader } from '@/components/app';

import coatOfArms from '@/assets/images/pdf/coatOfArms.png';
import economicPositioningHeaderImg from '@/assets/images/pdf/economic-positioning-header.png';
import governanceHeaderImg from '@/assets/images/pdf/governance-header.png';
import integratedServicesProvisioningHeaderImg from '@/assets/images/pdf/integrated-services-provisioning-header.png';
import livingPlansLogoText from '@/assets/images/pdf/living-plans-logo-text.png';
import peopleDevelopmentHeaderImg from '@/assets/images/pdf/people-development-header.png';
import saDDM from '@/assets/images/pdf/saDDM.png';
import { useHttp, useDomPurify } from '@/composables';
import { projectLevels } from '@/const.js';
import {
  CatalyticProjects,
  CategoriesList,
  CatalyticProjectsAlignment,
  CatalyticProjectsDetails,
  CatalyticProjectsImpact,
  ListOfAbbreviations,
  ListOfFigures,
  CustomHeader,
  CoverPage,
  IndexPage,
} from '@/views/authenticated/plans/pdf/components';

const route = useRoute();
const domPurify = useDomPurify();

const plan = ref({});
const frameworks = ref([]);
const pdfData = ref({});
const settingsDictionary = ref({});
const figuresList = ref([]);

const pagedFlow = ref(null);
const allRenderableSections = ref([]);

const isPreLoading = ref(true);
const isLoadingPreview = ref(false);

let powerbiInstance = null;

const urlSearchParams = new URLSearchParams(window.location.search);
const isPrintMode = urlSearchParams.get('type') === 'print';

// Get value from settings, splitting by comma if necessary
const getSettingValue = (key) => {
  if (!settingsDictionary.value[key]) return null;
  return settingsDictionary.value[key].split(',')[0].trim();
};

const resolveAlignments = (nodeAlignments) => {
  const flattenedCategories = [];

  nodeAlignments.forEach(nItem => {
    const framework = frameworks.value.find(f => f.id === nItem.framework);
    if (!framework) return;

    const resolvedCategories = [];

    (nItem.categories || []).forEach(catId => {
      const category = framework.categories?.find(c => c.id === catId);
      if (!category) return;

      resolvedCategories.push({
        shortName: category.shortName,
        icon: framework.icon,
        backgroundColor: category.backgroundColor,
      });
    });

    if (resolvedCategories.length > 0) {
      flattenedCategories.push({
        shortName: framework.shortName,
        categories: resolvedCategories,
      });
    }
  });

  return flattenedCategories;
};
const generateReportContent = (settingsKey) => {
  const rawValue = settingsDictionary.value[settingsKey];
  if (!rawValue) return [];

  // Split string by comma, trim whitespace, and create a content item for each report ID
  return rawValue
    .split(',')
    .map(id => id.trim())
    .filter(id => id)
    .map(pageId => ({
      type: 'powerbi',
      pageId: pageId,
    }));
};

const transformApiDataToPdfStructure = (data) => {
  return [
    // {
    //   id: 'list-of-abbreviations',
    //   label: 'List of Abbreviations',
    //   content: [
    //     { type: 'table', rows: data?.abbreviations },
    //   ],
    // },
    {
      id: 'list-of-figures',
      label: 'List of Figures',
      breakBefore: true,
      content: [
        { type: 'table', rows: figuresList },
      ],
    },
    {
      id: 'executive-summary',
      label: 'Executive Summary',
      breakBefore: true,
      content: [
        { type: 'tinymce', value: data.executiveSummary },
      ],
    },
    {
      id: 'introduction',
      label: 'Introduction',
      breakBefore: true,
      content: [
        { type: 'tinymce', value: data.introduction },
      ],
    },
    {
      id: 'diagnostics',
      label: 'Diagnostics',
      breakBefore: true,
      content: [
        { type: 'heading', value: data.diagnostics.heading },
        { type: 'tinymce', value: data.diagnostics.content },
      ],
      subsections: [
        {
          id: 'people-development',
          label: 'People Development',
          labelImg: peopleDevelopmentHeaderImg,
          breakBefore: true,
          content: generateReportContent('powerbi-pageids-people-development'),
        },
        {
          id: 'economic-positioning',
          label: 'Economic Positioning',
          labelImg: economicPositioningHeaderImg,
          breakBefore: true,
          content: generateReportContent('powerbi-pageids-economic-positioning'),
        },
        {
          id: 'integrated-services-provisioning',
          label: 'Integrated Services Provisioning',
          labelImg: integratedServicesProvisioningHeaderImg,
          breakBefore: true,
          content: generateReportContent('powerbi-pageids-integrated-services-provisioning'),
        },
        {
          id: 'governance-financial-management',
          label: 'Governance & Financial Management',
          labelImg: governanceHeaderImg,
          breakBefore: true,
          content: generateReportContent('powerbi-pageids-governance-and-financial-management'),
        },
        {
          id: 'data-sources',
          label: 'Data Sources',
          breakBefore: true,
          content: generateReportContent('powerbi-pageids-data-sources'),
        },
      ].filter(section => section.content && section.content.length > 0), // Only show sections that have reports
    },
    {
      id: 'district-vision',
      label: data.vision[0].heading,
      breakBefore: true,
      content: (data.vision[0]?.questions || []).flatMap(q => ([
        { type: 'heading', value: q.title },
        { type: 'tinymce', value: q.content },
      ])),
      subsections: (data.vision || []).slice(1).map((item, idx) => ({
        id: `vision-subsection-${idx}`,
        label: item.heading,
        content: (item.questions || []).flatMap(q => ([
          { type: 'heading', value: q.title },
          { type: 'tinymce', value: q.content },
        ])),
      })),
    },
    {
      id: 'catalytic-projects',
      label: 'Catalytic Projects',
      breakBefore: true,
      content: (projectLevels).map(pl => ({
        type: 'card',
        value: pl,
      })),
      subsections: projectLevels.map((pl, plIdx) => {
        // find all projects that belong to this level
        const matchingProjects = (data.nodes || []).filter(n => n.data?.level === pl.level);

        return {
          id: `catalytic-project-level-${plIdx + 1}`,
          label: pl.fullName,
          isCategory: true,
          breakBefore: true,
          customHeaderData: {
            text: `${pl.level} PROJECT`,
          },
          subsections: matchingProjects.map((n, nIdx) => ({
            id: `catalytic-project-${plIdx + 1}-${nIdx + 1}`,
            label: `${plIdx + 1}.${nIdx + 1} ${n.label}`,
            breakBefore: nIdx > 0,
            content: [
              {
                type: 'tinymce',
                value: n.data?.writeUp,
              },
              {
                type: 'table',
                style: 'projectDetails',
                rows: (() => {
                  const { startDate, endDate, ...rest } = n.data?.projectDetails;
                  return {
                    ...rest,
                    timeframe: { startDate: startDate ?? null, endDate: endDate ?? null },
                    stakeholders: n.data?.stakeholders,
                  };
                })(),
              },
              {
                type: 'table',
                style: 'developmentImpact',
                rows: n.data?.developmentImpact,
              },
              {
                type: 'table',
                style: 'alignments',
                rows: resolveAlignments(n.data?.alignments),
              },
            ],
          })),
        };
      }),
    },
    {
      id: 'conclusion',
      label: 'Conclusion',
      breakBefore: true,
      content: [
        { type: 'tinymce', value: data.conclusion },
      ],
    },
    {
      id: 'appendix',
      label: 'Appendix',
      breakBefore: true,
      subsections: (frameworks.value || []).map(f => ({
        id: `framework-${f.id}`,
        label: f.name,
        breakBefore: true,
        content: [
          { type: 'link-button', url: f.url },
          { type: 'tinymce', value: f.writeUp },
          {
            type: 'list',
            context: { framework: f.name },
            items: (f.categories || []).map(c => ({
              name: c.name,
              writeUp: c.writeUp,
              icon: c.icon,
              backgroundColor: c.backgroundColor,
            })),
          },
        ],
      })),
    },
  ];
};

const fetchFrameworkDetails = async () => {
  const response = await useHttp().get('util/frameworks');

  if (response.statusCode === 200 && response.data.items) {
    frameworks.value = response.data.items;
  }
};

const loadPlan = async () => {
  // Load Settings
  const settingsResponse = await useHttp().get('/settings');

  if (settingsResponse.statusCode === HttpStatusCode.Ok) {
    settingsDictionary.value = settingsResponse.data.items.reduce((acc, item) => {
      acc[item.key] = item.values;
      return acc;
    }, {});
  }

  // Load plan
  const response = await useHttp().get(`/districts/${route.params.district}/plans/${route.params.plan}`);

  if (response.statusCode === HttpStatusCode.Ok) {
    const data = response.data;
    plan.value = data;

    pdfData.value.coatOfArms = coatOfArms;
    pdfData.value.saDDM = saDDM;
    pdfData.value.livingPlansLogoText = livingPlansLogoText;

    pdfData.value.district = data.district;
    pdfData.value.logo = data.logo;
    pdfData.value.provincialLogo = data.provincialLogo;
    pdfData.value.theme = data.headingColour || '#2E2F90';
    pdfData.value.lastPublishedAt = '2026-02-04T09:52:49.117Z';

    await fetchFrameworkDetails();

    data.diagnostics = {
      heading: 'District Narrative Profile',
      content: '<p>The Diagnostics section of the Living One Plan will feature the District’s Narrative Profile. The District Narrative Profile offers an overview of the District Space, with key indicators across the themes of <strong>People, Economy, Governance,</strong> and <strong>Services</strong>. The Profile contains visualisations and corresponding ai-generated narrative summaries. </p>' +
               '<p><br></p>' +
               '<p><br></p>' +
               '<p>Drawing on a wide range of data sources, Narrative Profiles are periodically updated to reflect the latest available information. Replacing static situational analyses that offer only a snapshot in time, the District Narrative Profile provides readers of the Living DDM One Plan with a current and informed understanding of district dynamics. </p>' +
               '<p><br></p>' +
               '<p><br></p>' +
               '<p>Importantly, the insights derived from the district profile should inform the strategies, outcomes and programmes contained in the One Plan.</p>',
    };
    // data.abbreviations = [
    //   { abbreviation: 'ttyl', fullName: 'talk to you later' },
    //   { abbreviation: 'yolo', fullName: 'you only live once' },
    // ];

    pdfData.value.contentSections = transformApiDataToPdfStructure(data);

    console.log('plan', plan.value);
    console.log('pdfData', pdfData.value);
  }
};

function flattenSectionsHelper(sections, level = 0, targetArray = [], rootId = null, parent = null) {
  sections.forEach((section, index) => {
    if (section.content) {
      section.content.forEach(item => {
        if (item.type === 'tinymce' && item.value) {
          // safer than regex
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = item.value;

          // create an array of figures, storing an id in the array and on the figure
          // outputted in the contentSections loop so we can nav via the index page
          const figures = tempDiv.querySelectorAll('figure');

          figures.forEach((figure) => {
            const caption = figure.querySelector('figcaption');
            const captionText = (caption.innerText && caption.innerText !== 'Insert figure name here') ? caption.innerText : 'No caption provided';

            if (caption) {
              const figNum = figuresList.value.length + 1;
              const figId = `fig-${figNum}`;
              const figCaptionText = `Figure ${figNum}: ${captionText}`;

              // Add ID to the DOM element
              figure.id = figId;
              caption.innerText = figCaptionText;

              // Add to your list
              figuresList.value.push({
                id: figId,
                text: figCaptionText,
              });
            }
          });

          // break all p tags that are too big for pagedjs to split to the next page
          const pTags = tempDiv.querySelectorAll('p');
          pTags.forEach(p => {
            if (p.innerText.length > 1500) {
              const htmlContent = p.innerHTML;
              const segments = [];
              const size = 100;

              for (let i = 0; i < htmlContent.length; i += size) {
                segments.push(htmlContent.substring(i, i + size));
              }

              p.outerHTML = `<p>${segments.join('</p><p>')}</p>`;
            }
          });

          // update the item.value with the new html that has the figures with ids
          item.value = tempDiv.innerHTML;
        }
      });
    }

    targetArray.push({
      ...section,
      level: level,
      rootId: rootId,
      parent: parent,
      sectionNumber: index + 1,
    });

    if (Array.isArray(section.subsections) && section.subsections.length) {
      // extract the unimportant keys
      const { breakBefore, content, subsections, ...flatParent } = section;

      flattenSectionsHelper(section.subsections, level + 1, targetArray, (rootId ?? section.id), flatParent);
    }
  });
  return targetArray;
}

onMounted(async () => {
  await loadPlan();
  document.body.style.setProperty('--theme-color', pdfData.value.theme);

  await waitForImagesToLoad();

  allRenderableSections.value = flattenSectionsHelper(pdfData.value.contentSections, 0, []);

  isPreLoading.value = false;
  isLoadingPreview.value = true;

  const previewer = new Previewer();
  pagedFlow.value = await previewer.preview();

  // get the container of all pagedjs pages so we can apply styles to it based on the hash
  const pagedJsContainer = document.querySelector('.pagedjs_pages');

  await loadPowerBi(pagedJsContainer);

  if (isPrintMode) {
    const pages = document.querySelectorAll('.content-to-print');
    const pageOrientations = [];

    pagedJsContainer.classList.add('print');

    // make an array of each pages orientation
    pages.forEach((page) => {
      const rect = page.getBoundingClientRect();

      if (rect.width > rect.height) {
        pageOrientations.push('landscape');
      } else {
        pageOrientations.push('portrait');
      }
    });

    // expose array of orientations to Puppeteer so it can choose the correct pages from each buffer
    window.pageOrientations = pageOrientations;

    // Map all pages to find their order
    const sheets = Array.from(document.querySelectorAll('.pagedjs_sheet'));

    // Find all internal links (cuz pdf-lib destroys them when it merges pages together)
    const linksToInject = [];
    const internalLinks = document.querySelectorAll('.pagedjs_pages a[href^="#"]');

    internalLinks.forEach(link => {
      // Find the link's physical location (Source Page)
      const linkSheet = link.closest('.pagedjs_sheet');
      if (!linkSheet) return;

      const sourcePageIndex = sheets.indexOf(linkSheet);

      // Find the target's physical location (Destination Page)
      const hash = link.getAttribute('href'); // e.g., "#introduction"
      if (!hash) return;

      // Sanitize hash to find ID
      const targetId = hash.replace('#', '');
      const targetEl = document.getElementById(targetId);

      // If target exists in the print DOM
      if (targetEl) {
        const targetSheet = targetEl.closest('.pagedjs_sheet');
        if (targetSheet) {
          const targetPageIndex = sheets.indexOf(targetSheet);

          // Calculate Coordinates as Percentages (0 to 1)
          const linkRect = link.getBoundingClientRect();
          const sheetRect = linkSheet.getBoundingClientRect();

          // Relative position inside the sheet
          const relativeX = linkRect.left - sheetRect.left;
          const relativeY = linkRect.top - sheetRect.top;

          linksToInject.push({
            sourcePageIndex, // Which page is the link ON?
            targetPageIndex, // Which page does it go TO?
            // Save coordinates as percentages of the page size
            xPct: relativeX / sheetRect.width,
            yPct: relativeY / sheetRect.height,
            wPct: linkRect.width / sheetRect.width,
            hPct: linkRect.height / sheetRect.height,
          });
        }
      }
    });

    // expose array of links to Puppeteer so it can create new links for all the pages after pdf-lib merges them
    window.pdfLinks = linksToInject;

    isLoadingPreview.value = false;

    await nextTick();
    window.pdfReady = true;
  }
});

onUnmounted(() => {
  const powerbiContainers = document.querySelectorAll('.powerbi-container');

  if (powerbiInstance && powerbiContainers.length > 0) {
    for (const container of powerbiContainers) {
      powerbiInstance.reset(container);
    }
  }
});

const loadPowerBi = async (pagedJsContainer) => {
  const powerbiContainers = document.querySelectorAll('.pagedjs_pages .powerbi-container');

  if (powerbiContainers.length === 0) {
    console.error('nothing to embed');
    return;
  }

  const response = await useHttp().get('/profile/token');
  const token = response.data;

  // Get dynamic Report ID and Lookup Column from settings
  const reportId = getSettingValue('powerbi-report-id') || token.reportId;
  const lookupColumn = getSettingValue('powerbi-region-lookup-selector') || 'District';
  const filterValue = pdfData.value.district?.powerBiRegionLookupValue;

  const reportLevelFilter = {
    $schema: 'http://powerbi.com/product/schema#basic',
    target: { table: 'Lookup', column: lookupColumn },
    operator: 'In',
    values: [filterValue],
  };

  const baseEmbedConfig = {
    type: 'report',
    id: reportId,
    embedUrl: token.embedUrl,
    accessToken: token.embedToken,
    tokenType: pbi.models.TokenType.Embed,
    settings: {
      usePhasedEmbedding: true,
      layoutType: pbi.models.LayoutType.Custom,
      customLayout: { displayOption: pbi.models.DisplayOption.FitToPage },
      panes: { filters: { expanded: false, visible: false }, pageNavigation: { visible: false } },
      background: pbi.models.BackgroundType.Transparent,
    },
    filters: [reportLevelFilter],
  };

  try {
    await loadPowerBiScript();
    // The script is now loaded and `window.powerbi` is available
    powerbiInstance = new pbi.service.Service(
      pbi.factories.hpmFactory,
      pbi.factories.wpmpFactory,
      pbi.factories.routerFactory,
    );

    // stack all pages on top of eachother so lazy loaded elements can load
    if (isPrintMode) {
      pagedJsContainer.classList.add('loading-powerbi-layout');
    }

    const containersArray = Array.from(powerbiContainers);
    // how many reportPages to load at once
    const BATCH_SIZE = 4;

    for (let i = 0; i < containersArray.length; i += BATCH_SIZE) {
      const batch = containersArray.slice(i, i + BATCH_SIZE);

      const batchPromises = batch.map((container) => {
        return new Promise((resolve) => {
          const settingsKey = container.dataset.pageName;

          const pageName = container.dataset.powerbiPageId;

          if (!pageName) {
            console.warn(`No setting value found for key: ${settingsKey}`);
            container.innerHTML = `<div class="powerbi-error-text">Configuration Missing: ${settingsKey}</div>`;
            resolve(true);
            return;
          }

          // PowerBI "pageName" expects the internal GUID/Name
          const embedConfig = {
            ...baseEmbedConfig,
            pageName: pageName,
          };

          try {
            const report = powerbiInstance.embed(container, embedConfig);

            if (isPrintMode) {
              report.on('rendered', () => {
                report.off('rendered');
                resolve(true);
              });
            } else {
              // In preview mode, resolve immediately to allow batching to continue
              resolve(true);
            }
          } catch (e) {
            console.error(`Failed to embed report ${container.id}`, e);
            resolve(true);
          }
        });
      });

      // Wait for this batch to finish before starting the next one
      await Promise.all(batchPromises);
    }
  } catch (error) {
    console.error('PowerBI Loading Error:', error);
  } finally {
    if (isPrintMode) {
      pagedJsContainer.classList.remove('loading-powerbi-layout');
    }
  }
};

// A function to load the script and return a promise
const loadPowerBiScript = () => {
  return new Promise((resolve, reject) => {
    // Check if the script is already loaded
    if (window.powerbi) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/powerbi-client/dist/powerbi.min.js';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load the Power BI script.'));
    document.head.appendChild(script);
  });
};

// gets all pageNames in all report sections
// const fetchReportPageIds = async (token) => {
//   return new Promise((resolve) => {
//     const scout = document.createElement('div');
//
//     scout.style.visibility = 'hidden';
//     scout.style.position = 'absolute';
//     scout.style.top = '0';
//     scout.style.width = '1px';
//     scout.style.height = '1px';
//
//     document.body.appendChild(scout);
//
//     const config = {
//       type: 'report',
//       id: token.reportId,
//       embedUrl: token.embedUrl,
//       accessToken: token.embedToken,
//       tokenType: pbi.models.TokenType.Embed,
//       settings: {
//         panes: { filters: { visible: false }, pageNavigation: { visible: false } },
//       },
//     };
//
//     // Embed
//     const report = powerbiInstance.embed(scout, config);
//
//     report.on('loaded', async () => {
//       try {
//         // list all pages
//         const pages = await report.getPages();
//
//         // map each displayName with id name (displayName is human readable and should always be the same)
//         const map = {};
//         pages.forEach(p => {
//           map[p.displayName] = p.name;
//         });
//
//         // remove scout and return the map of all pages in the report
//         report.off('loaded');
//         powerbiInstance.reset(scout);
//         document.body.removeChild(scout);
//
//         console.log(pages);
//         console.log(map);
//         resolve(map);
//       } catch (e) {
//         console.error('Error extracting pages', e);
//         resolve({});
//       }
//     });
//
//     // Safety timeout
//     setTimeout(() => {
//       if (document.body.contains(scout)) {
//         console.warn('Scout timed out');
//         document.body.removeChild(scout);
//       }
//       resolve({});
//     }, 30000);
//   });
// };

// necessary cuz images need to be loaded before paged.js runs
function waitForImagesToLoad() {
  const images = Array.from(document.images);

  const imagePromises = images.map(img => {
    if (img.complete) return Promise.resolve();
    return new Promise(resolve => {
      img.onload = resolve;
      img.onerror = resolve;
    });
  });

  const timeoutPromise = new Promise(resolve => setTimeout(resolve, 5000));

  // Wait for images OR timeout
  return Promise.race([Promise.all(imagePromises), timeoutPromise]);
}

// used by index-page
function goToSection(id, event) {
  // Prevent the native HTML jump that breaks the Paged.js layout
  if (event) event.preventDefault();

  const targetEl = document.getElementById(id);

  if (targetEl) {
    const targetSheet = targetEl.closest('.pagedjs_sheet');

    if (targetSheet) {
      targetSheet.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
}

// duplicates .theads on page breaks
class RepeatingTableHeader extends Handler {
  constructor(chunker, polisher, caller) {
    super(chunker, polisher, caller);
  }
  afterPageLayout(pageElement) {
    // find every table fragment that gets broken to a new page
    const splitTables = pageElement.querySelectorAll('.table[data-split-from]');

    splitTables.forEach((tableFragment) => {
      const parentSection = tableFragment.closest('section');
      const isFinancialPlan = parentSection?.getAttribute('data-page-root-id') === 'financial-plan';

      // only do this for the financial plan tables
      if (!isFinancialPlan) return;

      // safety check not to infinite loop
      if (tableFragment.previousElementSibling?.classList.contains('continued-label')) return;

      const ref = tableFragment.getAttribute('data-ref');

      // look for the original table header in the master document using the ref we generated
      const sourceTable = document.querySelector(`.table[data-ref="${ref}"]:not([data-split-from])`);

      if (sourceTable) {
        // put the '{section label} Cont.' above the table
        const sectionLabel = parentSection?.getAttribute('data-label');
        if (sectionLabel) {
          const labelDiv = document.createElement('div');
          labelDiv.className = 'continued-label';
          labelDiv.innerHTML = `${sectionLabel} Cont.`;
          tableFragment.parentNode.insertBefore(labelDiv, tableFragment);
        }

        // clone the table header and put it inside the new table fragment
        const thead = sourceTable.querySelector('.thead');
        if (thead) {
          const clonedHeader = thead.cloneNode(true);
          clonedHeader.classList.add('cloned-thead');
          tableFragment.insertBefore(clonedHeader, tableFragment.firstChild);
        }
      }
    });
  }
}
registerHandlers(RepeatingTableHeader);
</script>

<style lang="scss">
@media print {
  * {
    transition: none !important;
  }

  body {
    span, strong, b, a {
      line-height: normal !important;
      font-size: inherit;
    }
  }

  .loading-powerbi-layout {
    .pagedjs_pages {
      position: relative !important;
      height: 100vh !important;
      overflow: hidden !important;
    }

    .pagedjs_sheet {
      position: absolute !important;
      top: 0 !important;
      left: 0 !important;
      z-index: 1 !important;

      &::after {
        content: 'Loading...';
        width: 100%;
        height: 100%;
        padding: 10px;
        font-size: 28px;
        position: absolute !important;
        top: 0 !important;
        left: 0 !important;
        z-index: 99;
        background-color: white;
      }
    }
  }

  .pagedjs_pages:not(.print) {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5mm;
    padding-top: 5mm;
    padding-bottom: 5mm;

    .pagedjs_sheet {
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
    }

    // required for all landscape pages
    //.pagedjs_performanceTargetsPage_page {
    //  width: 297mm !important;
    //  height: 210mm !important;
    //  min-width: 297mm !important;
    //
    //  .pagedjs_sheet {
    //    width: 297mm !important;
    //    height: 210mm !important;
    //  }
    //}
  }

  .content-to-print {
    counter-reset: mainSectionNumber 0;

    > section:first-child:not([data-page-root-id=diagnostics]) {
      padding-top: 11mm !important;
    }
  }

  // general
  .pagedjs_sheet {
    position: relative;
    background-color: white;
    font-size: 8pt;
  }

  .pagedjs_margin-content {
    height: 100%;
  }

  section {
    font-family: 'Inter' !important;

    &[data-split-from] {
      > :first-child {
        margin-top: 0;
      }

      .continued-label {
        font-size: 14pt;
        font-weight: 600;
        font-style: italic;
        color: #000;
        margin-bottom: 8mm;
      }

      &[data-page-root-id="catalytic-projects"], &[data-page-root-id="appendix"] {
        > :first-child, > div > ul:first-child > li:first-child  {
          flex-wrap: wrap;

          &::before {
            content: var(--label) " (continued)";
            display: block;
            width: 100%;
            flex-basis: 100%;
            order: -1;
            font-size: 10pt;
            font-weight: 600;
            font-style: italic;
            color: var(--theme-color);
            margin-bottom: 10mm;
          }
        }
      }
    }

    &.page {
      padding-inline: 12mm;

      &.has-content {
        height: 100%;
        max-height: 100%;
      }
    }

    &.break-before {
      break-before: page;
    }

    &.subsection {
      h2 {
        color: var(--theme-color);
      }
    }

    figure {
      margin-bottom: 2mm;

      img {
        width: 100%;
        max-height: 150mm;
        object-position: left;
        object-fit: fill;
      }

      figcaption {
        font-size: 9pt;
        font-style: italic;
        font-weight: 600;
        margin-top: 1mm;
      }
    }

    ul, ol {
      padding-left: 24px;
      white-space: normal;
    }

    ul {
      list-style: disc;
    }

    ol {
      list-style: decimal;
    }

    br + ul, br + ol {
      margin-top: -1mm;
    }

    li:not(.index-page-item):not(.index-page-category-item):not([data-undisplayed="undisplayed"]), p {
      width: fit-content;
      font-size: 10pt;
      line-height: 1.5;
      margin-bottom: 0;
      font-weight: 400;
      color: #111;
    }

    li {
      margin-bottom: 1mm;

      p {
        margin-bottom: 0;
      }
    }

    b, strong {
      color: #000;
    }

    .tinymce-content p {
      display: inline;
    }

    .tinymce-content + h3 {
      margin-top: 10mm;
    }

    &.category {
      h1 {
        display: none !important;
      }
    }

    h1 {
      font-size: 30pt;
      line-height: normal;
      font-weight: 700;
      margin-bottom: 11mm;
      color: var(--theme-color);
      display: flex;
      align-items: center;
      string-set: footer-left-after-content content(); // capture content for footer
    }
    &:not([data-page-id="cover"]):not([data-page-id="index-page"]):not([data-page-id="list-of-abbreviations"]):not([data-page-id="list-of-figures"]) h1 { // create number next to h1s
      counter-increment: mainSectionNumber;

      &::before {
        content: counter(mainSectionNumber);
        border-radius: 99px;
        width: 16mm;
        height: 16mm;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        line-height: 10mm;
        background-color: var(--theme-color);
        color: #fff;
        font-weight: 600;
        font-size: 22pt;
        margin-right: 4mm;
      }
    }

    h2 {
      font-size: 20pt;
      color: var(--theme-color);
      margin-bottom: 7mm;
      line-height: normal;
      break-after: avoid;
    }

    div.tinymce-content + h2 {
      margin-top: 9mm;
    }

    h3 { // wizywig h1
      font-size: 13pt;
      color: #000;
      margin-bottom: 3mm;
      line-height: normal;
      break-after: avoid;
      font-weight: 700;
    }

    h4 { // wizywig h2
      font-size: 12pt;
      color: #000;
      margin-bottom: 1mm;
      line-height: normal;
      break-after: avoid;
      font-weight: 600;
    }

    h5 { // wizywig h3
      font-size: 12pt;
      color: #000;
      margin-bottom: 1mm;
      line-height: normal;
      break-after: avoid;
      font-weight: 600;
    }

    h1 + .document-link {
      margin-top: -18mm;
    }

    .document-link + :not(.document-link) {
      margin-top: 20mm;
    }

    //tables are super glitchy and dont break properly
    .table {
      display: block;
      width: 100%;
      border: 1px solid #bcbec0;
      font-size: 10pt;
      color: #000;

      .thead {
        .th {
          display: flex;
          flex-direction: column;
          justify-content: end;
          font-weight: 700;
          padding: 8px;
        }
      }

      .tbody {
        font-weight: 500;
      }

      .tr {
        display: flex;
        flex-direction: row;
        width: 100%;
        break-inside: avoid;

        &:not(:last-child) {
          border-bottom: 1px solid #bcbec0;
        }
      }

      .th, .td {
        padding: 5px 10px 4px 10px;
        flex: 1;
        min-width: 0;
        word-wrap: break-word;
        overflow-wrap: break-word;
        line-height: normal;

        &:not(:last-child) {
          border-right: 1px solid #bcbec0;
        }

        &:empty::before {
          content: "\00a0";
        }
      }
    }

    .powerbi-container {
      /* A4 is 297mm high. Subtracting ~60mm for margins/headers leaves ~220mm */
      //height: 220mm !important;
      width: 100% !important;
      overflow: hidden !important;

      .powerbi-error-text {
        color: red;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }

    .website-btn {
      display: flex;
      align-items: center;
      width: fit-content;
      gap: 8px;
      background-color: var(--theme-color);
      color: #fff !important;
      text-decoration: none !important;
      padding: 10px 20px;
      border-radius: 4px;
      font-weight: 700;
      font-size: 10.5pt;
      margin-bottom: 9mm;
      cursor: pointer;

      .arrow {
        font-size: 1.2em;
        line-height: 1;
        margin-left: 5px;
      }

      print-color-adjust: exact;
      -webkit-print-color-adjust: exact;
    }
  }

  // pages
  section {
    &[data-page-id="cover"] {
      page: coverPage;
      break-inside: avoid;
      break-after: avoid;
    }

    &[data-page-id="index-page"] {
      page: indexPage;

      ul, li {
        padding: 0;
        margin: 0;
      }

      .index-page-item, .index-page-category-item {
        display: flex;
        align-items: baseline;

        .index-page-title, .index-page-page-link-number {
          color: #111;
          text-decoration: none !important;
          white-space: normal !important;
        }

        .index-page-title {
          flex: 0 0 auto;
          max-width: 79%;
        }

        .index-page-dots {
          flex-grow: 1;
          margin: 0 6px;
          overflow: hidden;
          white-space: nowrap;
          letter-spacing: 0.8px;
          color: #000;

          &::after {
            content: ".............................................................................................................................................................................................................................";
          }
        }

        .index-page-page-link-number::after {
          font-weight: 600;
          content: target-counter(attr(href), page);
        }

        /* Level-based styles */
        &[data-level="0"] {
          margin-bottom: 10px;
          font-size: 12pt !important;

          .index-page-title {
            color: #000;
            font-weight: 700;
          }

          .index-page-page-link-number {
            font-size: 11pt !important;
            font-weight: 600;
          }
        }

        &[data-level="1"] {
          margin-bottom: 8px;
          font-size: 10pt !important;

          .index-page-title {
            color: #222;
            font-weight: 600;
          }
        }

        &[data-level="2"] {
          margin-bottom: 6px;
          font-size: 9pt !important;

          .index-page-title {
            color: #222;
            font-weight: 400;
          }

          .index-page-dots {
            letter-spacing: 0.5px;
          }
        }
      }

      .index-page-category-item {
        margin-top: 10px;
        margin-bottom: 10px;
      }

      .index-page-item[data-level="0"] + .index-page-category-item {
        margin-top: 0 !important;
      }

      .index-page-item[data-level="2"] + .index-page-item[data-level="0"] {
        margin-top: 4px !important;
      }
    }

    &[data-page-id="list-of-abbreviations"] {
      page: listOfAbbreviationsPage;

      .table {
        .tr .td:first-child {
          flex: 0 0 16%;
        }
      }
    }

    &[data-page-id="list-of-figures"] {
      page: listOfFiguresPage;

      .table {
        .tr .td:last-child {
          flex: 0 0 8%;
          text-align: end;
        }

        a {
          text-decoration: none;
          color: inherit;
          height: 100%;
          width: 100%;

          &::after {
            content: target-counter(attr(href), page);
          }
        }
      }
    }

    &[data-page-id="introduction"] {
      page: introductionPage;
    }

    &[data-page-id="diagnostics"] {
      page: diagnosticsPage;

      h2 {
        color: #000;
      }
    }

    &[data-page-root-id="diagnostics"] {
      page: diagnosticsSubsectionPage;
      padding: 0 !important;

      h2 {
        display: none;
      }
    }

    &[data-page-id="district-vision"] {
      page: districtVisionPage;
    }

    &[data-page-root-id="district-vision"] {
      page: districtVisionSubsectionPage;

      h2 {
        margin-bottom: 16mm;
      }
    }

    &[data-page-id="catalytic-projects"] {
      page: catalyticProjectsPage;

      h1 {
        margin-bottom: 27mm;
      }

      .card {
        position: relative;
        background-color: #f7f5f5 !important;
        border-radius: 16px;
        padding: 6mm;
        margin-bottom: 5mm;
        display: flex;
        gap: 4mm;
        break-inside: avoid;

        &::before {
          content: "PROJECT LEVEL DESCRIPTIONS";
          position: absolute;
          top: -11mm;
          left: 0;
          font-size: 10pt;
          font-weight: 600;
          text-transform: uppercase;
          color: #555;
          letter-spacing: 0.5px;
        }

        & ~ .card::before {
          display: none;
        }

        > div:first-child {
          width: 35%;

          .level-badge {
            padding: 1mm 3mm;
            border-radius: 99px;
            color: white;
            font-weight: 700;
            font-size: 8pt;
            text-transform: uppercase;
            margin-bottom: 0.7em;
            width: fit-content;
            display: block;
          }

          .project-title {
            display: block;
            font-size: 13pt;
            font-weight: 700;
            margin-bottom: 5mm;
            color: #000;
          }

          .project-type-container {
            font-size: 10pt;
            font-weight: 500;

            b {
              display: block;
              line-height: 1.5 !important;
            }
          }
        }

        > div:last-child {
          font-size: 9pt;
          flex: 1;
          line-height: 1.4;

          * {
            display: inline;
            font-size: inherit;
            line-height: 1.1 !important;
          }
        }
      }
    }

    &[data-page-root-id="catalytic-projects"] {
      page: catalyticProjectsSubsectionPage;

      h4 {
        font-size: 10pt;
        font-weight: 600;
        text-transform: uppercase;
        color: #555;
        letter-spacing: 0.5px;
        margin-top: 8mm;
        margin-bottom: 3mm;
      }

      .details, .impact, .alignment {
        break-inside: avoid;

        .table .td {
          font-size: 9pt;
        }
      }

      .details {
        .table .tr .td {
          &:first-child {
            flex: 0 0 29%;
          }
        }
      }

      .impact {
        .table .tr .td {
          &:last-child {
            flex: 0 0 18%;
          }
        }
      }

      .alignment {
        .table .tr .td {
          padding-block: 6px;

          &:first-child {
            flex: 0 0 19%;
            display: flex;
            align-items: center;
          }

          &:last-child {
            display: flex;
            gap: 6px;
            flex-wrap: wrap;

            .alignment-badge {
              display: flex;
              align-items: center;
              padding: 2px;
              border-radius: 99px;
              font-size: 7.5pt;
              white-space: nowrap;
              height: 24px;

              img {
                border-radius: 99px;
              }

              span {
                padding: 0 4px;
              }
            }
          }
        }
      }
    }

    &[data-page-id="appendix"] {
      page: appendixPage;
      padding: 6mm 0 0 0 !important;
      height: 100%;

      h1 {
        margin: 0;
        border-radius: 20px;
        height: 100%;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: var(--theme-color);
        color: white;
        font-weight: 1000;
        font-size: 58pt;
        text-transform: uppercase;

        &::before {
          display: none !important;
        }
      }
    }

    &[data-page-root-id="appendix"] {
      page: appendixSubsectionPage;

      h3 {
        margin-top: 8mm;
        margin-bottom: 6mm;
        break-after: avoid;
      }

      ul {
        list-style: none;
        padding: 0;

        li {
          display: flex;
          break-inside: avoid;
          margin-bottom: 8mm !important;

          img {
            width: 24mm;
            height: 24mm;
            margin-right: 6mm;
          }

          .text-container {
            flex: 1;

            h3 {
              margin-top: 0;
              margin-bottom: 2mm;
            }

            div * {
              line-height: 1.4;
            }
          }
        }
      }
    }
  }

  // reuseable elements
  section {
    .header-left {
      position: running(headerLeft);
    }

    .dynamic-header-left {
      position: running(dynamicHeaderLeft);
    }

    .header-right {
      position: running(headerRight);
    }

    .footer {
      position: running(footer);
    }
  }

  .dynamic-header-left {
    .default {
      background-color: var(--theme-color);
      color: #fff;
      border-radius: 99px;
      height: fit-content;
      width: fit-content;
      white-space: nowrap;
      padding: 6px 14px;
      font-weight: 600;
      font-size: 9.8pt;
    }
  }

  .footer-left::after {
    content: " " string(footer-left-after-content);
    font-weight: 400;
    margin-left: -3px;
  }
  .footer-right::after {
    content: " " counter(page);
    font-weight: 500;
    font-size: 8pt;
    margin-left: 16px;
    margin-top: 3.5px;
  }

  @page {
    size: A4;
    margin: 30mm 8mm;

    @top-left {
      content: element(headerLeft);
    }
    @top-center {
      content: '';
    }
    @top-right {
      content: element(headerRight);
    }

    @bottom-left {
      content: element(footer);
    }
  }

  @page coverPage {
    margin: 0;

    @top-left {
      content: none;
    }
    @top-center {
      content: none;
    }
    @top-right {
      content: none;
    }
    @bottom-left {
      content: none;
    }
  }

  @page diagnosticsSubsectionPage {
    margin: 30mm 1mm 15mm 1mm;

    @top-left {
      content: element(dynamicHeaderLeft);
      padding-left: 6mm;
    }

    @top-right {
      content: element(headerRight);
      padding-right: 6mm;
    }

    @bottom-left {
      padding-left: 6mm;
      padding-right: 6mm;
    }
  }

  @page catalyticProjectsSubsectionPage {
    margin-bottom: 26mm;

    @top-left {
      content: element(dynamicHeaderLeft);
    }
  }

  @page appendixPage {
    margin-bottom: 26mm;
  }
}
</style>
